import React, { useState, useEffect } from 'react';
import { SPACE, FONT_SIZE, FONT_WEIGHT } from '../theme';
import './primitives.css';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  formatValue?: (val: number) => string;
  onChange: (val: number) => void;
  onCommit?: (val: number) => void;
  disabled?: boolean;
}

// Exact-fidelity literals carried over from the prior styled
// definition — these hues have no 1:1 theme token, so keeping the literal
// makes the de-style pass a visual no-op.
const ACCENT = '#fbbf24';
const HEADER_COLOR = '#a0aec0';
const TRACK_BG = 'rgba(255, 255, 255, 0.1)';

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  marginBottom: SPACE.lg,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACE.sm,
  fontSize: FONT_SIZE.md,
  color: HEADER_COLOR,
};

const labelStyle: React.CSSProperties = {
  fontWeight: FONT_WEIGHT.medium,
};

const valueStyle: React.CSSProperties = {
  color: ACCENT,
  fontVariantNumeric: 'tabular-nums',
};

const inputWrapperStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const baseSliderStyle: React.CSSProperties = {
  width: '100%',
  height: 4,
  borderRadius: 2,
  outline: 'none',
  margin: 0,
};

export const Slider: React.FC<SliderProps> = ({
  label,
  value,
  min,
  max,
  step = 1,
  formatValue,
  onChange,
  onCommit,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Sync when prop changes externally (e.g. choosing a different ability)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setLocalValue(val);
    onChange(val);
  };

  const handleMouseUp = () => {
    if (onCommit) {
      onCommit(localValue);
    }
  };

  const displayString = formatValue ? formatValue(localValue) : localValue.toString();

  // Calculate background fill percentage
  const percentage = ((localValue - min) / (max - min)) * 100;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={labelStyle}>{label}</div>
        <div style={valueStyle}>{displayString}</div>
      </div>
      <div style={inputWrapperStyle}>
        <input
          className="helix-slider"
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={handleChange}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
          disabled={disabled}
          style={{
            ...baseSliderStyle,
            background: `linear-gradient(to right, ${ACCENT} ${percentage}%, ${TRACK_BG} ${percentage}%)`,
          }}
        />
      </div>
    </div>
  );
};
